/**
 * IconFacade.js
 *
 * Vendor-agnostic icon facade.
 * - Owns semantic manifest lookup
 * - Delegates rendering/token translation to active adapter
 */
(function (global) {
	'use strict';

	/**
	 * Creates a facade instance bound to a manifest and adapter registry.
	 *
	 * @param {Object} options
	 * @param {Object} [options.manifest] semantic key -> canonical token map
	 * @param {string} [options.defaultAdapter] adapter name selected at startup
	 * @returns {Object} facade API
	 */
	function create(options) {
		var opts = options || {};
		var manifest = opts.manifest || {};
		var defaultAdapter = opts.defaultAdapter || '';
		var adapters = {};
		var activeAdapterName = defaultAdapter;

		/**
		 * Returns true when the semantic key exists in the manifest.
		 *
		 * @param {string} key
		 * @returns {boolean}
		 */
		function hasManifestKey(key) {
			return Object.prototype.hasOwnProperty.call(manifest, key);
		}

		/**
		 * Resolves the currently active adapter object.
		 *
		 * @returns {Object|null}
		 */
		function resolveAdapter() {
			return adapters[activeAdapterName] || null;
		}

		/**
		 * Converts semantic key input to manifest value, otherwise returns input as-is.
		 *
		 * @param {string} keyOrToken
		 * @returns {string}
		 */
		function resolveManifestOrInput(keyOrToken) {
			if (typeof keyOrToken === 'string' && hasManifestKey(keyOrToken)) {
				return manifest[keyOrToken];
			}
			return keyOrToken;
		}

		var api = {
			manifest: manifest,

			/**
			 * Registers an adapter implementation under a name.
			 *
			 * @param {string} name
			 * @param {Object} adapter
			 */
			registerAdapter: function (name, adapter) {
				if (!name || !adapter) {
					return;
				}
				adapters[name] = adapter;
				if (!activeAdapterName) {
					activeAdapterName = name;
				}
			},

			/**
			 * Switches the active adapter when the requested adapter exists.
			 *
			 * @param {string} name
			 */
			setAdapter: function (name) {
				if (adapters[name]) {
					activeAdapterName = name;
				}
			},

			/**
			 * Returns the currently selected adapter name.
			 *
			 * @returns {string}
			 */
			getAdapter: function () {
				return activeAdapterName;
			},

			/**
			 * Resolves canonical token using active adapter normalization.
			 *
			 * @param {string} keyOrToken semantic key, token, or class string
			 * @param {Object} [opt]
			 * @returns {string}
			 */
			getCanonicalToken: function (keyOrToken, opt) {
				var adapter = resolveAdapter();
				var raw = resolveManifestOrInput(keyOrToken);
				if (!adapter || typeof adapter.toCanonicalToken !== 'function') {
					return raw;
				}
				return adapter.toCanonicalToken(raw, opt, manifest);
			},

			/**
			 * Resolves provider-specific token from canonical token.
			 *
			 * @param {string} keyOrToken semantic key, token, or class string
			 * @param {Object} [opt]
			 * @returns {string}
			 */
			getToken: function (keyOrToken, opt) {
				var adapter = resolveAdapter();
				var canonical = this.getCanonicalToken(keyOrToken, opt);
				if (!adapter || typeof adapter.toToken !== 'function') {
					return canonical;
				}
				return adapter.toToken(canonical, opt, manifest);
			},

			/**
			 * Returns full provider-specific CSS class string for icon rendering.
			 *
			 * @param {string} keyOrToken semantic key, token, or class string
			 * @param {Object} [opt]
			 * @returns {string}
			 */
			getClass: function (keyOrToken, opt) {
				var adapter = resolveAdapter();
				var raw = resolveManifestOrInput(keyOrToken);
				if (!adapter || typeof adapter.toClass !== 'function') {
					return '';
				}
				return adapter.toClass(raw, opt, manifest);
			},

			/**
			 * Returns token intended for legacy rendering paths.
			 *
			 * @param {string} keyOrToken semantic key, token, or class string
			 * @param {Object} [opt]
			 * @returns {string}
			 */
			getLegacyToken: function (keyOrToken, opt) {
				var adapter = resolveAdapter();
				var raw = resolveManifestOrInput(keyOrToken);
				if (!adapter || typeof adapter.toLegacyToken !== 'function') {
					return this.getToken(keyOrToken, opt);
				}
				return adapter.toLegacyToken(raw, opt, manifest);
			},

			/**
			 * Renders an <i> element string using the active adapter class output.
			 *
			 * @param {string} keyOrToken semantic key, token, or class string
			 * @param {Object} [opt]
			 * @returns {string}
			 */
			renderI: function (keyOrToken, opt) {
				return '<i class="' + this.getClass(keyOrToken, opt) + '" aria-hidden="true"></i>';
			}
		};

		return api;
	}

	global.TMS_IconFacade = {
		create: create
	};
})(typeof window !== 'undefined' ? window : this);
